import DOMPurify from 'dompurify';
import katex from 'katex';

const FENCED_CODE_RE = /(```[\s\S]*?```)/g;
const DISPLAY_DOLLAR_MATH_RE = /\$\$([\s\S]{1,2000}?)\$\$/g;
const INLINE_DOLLAR_MATH_RE = /\$(?!\$)([^$\n]{1,500}?)\$/g;
const DISPLAY_BRACKET_MATH_RE = /\\\[([\s\S]*?)\\]/g;
const INLINE_PAREN_MATH_RE = /\\\(([\s\S]*?)\\\)/g;
const MAX_MATH_REPLACEMENTS = 200;

/** True when delimited content is likely LaTeX, not a currency amount. */
export function looksLikeMath(inner: string): boolean {
    const trimmed = inner.trim();
    if (!trimmed) {
        return false;
    }
    if (/^\d+(?:\.\d+)?(?:\s*(?:USD|usd|dollars?|cents?))?$/.test(trimmed)) {
        return false;
    }
    if (/^\\(?:[a-zA-Z]+|[^a-zA-Z])/.test(trimmed)) {
        return true;
    }
    if (/[\^_{}]/.test(trimmed)) {
        return true;
    }
    if (/\\frac|\\sqrt|\\Delta|\\pi|\\neq|\\pm|\\dfrac|\\cdot|\\text|\\gt|\\lt/.test(trimmed)) {
        return true;
    }
    if (/[=<>±≠≤≥]/.test(trimmed)) {
        return true;
    }
    // A 3+ letter word signals English prose, not algebra. Catches two currency
    // markers bracketing a sentence (e.g. "$5k+ standalone flags, $50k+"), whose
    // captured span would otherwise pass the digit-adjacent rule below via "5k".
    // Real inline math reaching this point ("3x", "2n+1") has no such word; LaTeX
    // commands and \text{...} are already handled by the checks above.
    if (/[a-zA-Z]{3,}/.test(trimmed)) {
        return false;
    }
    // Variable directly adjacent to a digit (e.g. "3x", "2n+1") is algebraic.
    // Adjacency (no space) avoids matching currency-with-words like "5 million".
    // ponytail: still misses "f(x)", "a + b", "n!"; expand the operator set if those matter.
    if (/\d[a-zA-Z]|[a-zA-Z]\d/.test(trimmed)) {
        return true;
    }
    if (/^[a-zA-Z]$/.test(trimmed)) {
        return true;
    }
    return false;
}

// KaTeX renders to HTML spans plus a MathML mirror, both carrying inline styles.
// Whitelist that structure so DOMPurify keeps it; everything else stays at defaults.
const KATEX_TAGS = [
    'math',
    'semantics',
    'annotation',
    'mrow',
    'mi',
    'mo',
    'mn',
    'msup',
    'msub',
    'msubsup',
    'mfrac',
    'msqrt',
    'mroot',
    'mover',
    'munder',
    'munderover',
    'mtable',
    'mtr',
    'mtd',
    'mspace',
    'mtext',
];
const KATEX_ATTRS = ['class', 'style', 'aria-hidden', 'encoding', 'mathvariant', 'xmlns', 'target'];

/**
 * Sanitizes rendered HTML, preserving KaTeX output. Registered as marked's
 * postprocess hook (see app.config) so it runs after markdown -> HTML - the only
 * XSS guard for the chat markdown path, which renders with disableSanitizer.
 */
export function sanitizeRenderedHtml(html: string): string {
    return DOMPurify.sanitize(html, { ADD_TAGS: KATEX_TAGS, ADD_ATTR: KATEX_ATTRS });
}

function renderKatexHtml(tex: string, displayMode: boolean): string {
    try {
        const html = katex.renderToString(tex.trim(), {
            displayMode,
            throwOnError: false,
            strict: 'ignore',
        });
        return displayMode ? `<div class="katex-display">${html}</div>` : html;
    } catch {
        return tex;
    }
}

function replaceMath(
    text: string,
    pattern: RegExp,
    displayMode: boolean,
    counter: { count: number },
    requireMathHeuristic: boolean
): string {
    return text.replace(pattern, (fullMatch, rawTex: string) => {
        if (counter.count >= MAX_MATH_REPLACEMENTS) {
            return fullMatch;
        }
        if (requireMathHeuristic && !looksLikeMath(rawTex)) {
            return fullMatch;
        }
        counter.count += 1;
        return renderKatexHtml(rawTex, displayMode);
    });
}

/**
 * Renders math to KaTeX HTML before marked runs.
 * Avoids ngx-markdown's renderMathInElement pass, which can crash Chrome on long LLM responses.
 */
export function renderMathInMarkdown(markdown: string): string {
    const counter = { count: 0 };

    return markdown
        .split(FENCED_CODE_RE)
        .map((segment, index) => {
            if (index % 2 === 1) {
                return segment;
            }
            let result = segment;
            result = replaceMath(result, DISPLAY_BRACKET_MATH_RE, true, counter, false);
            result = replaceMath(result, DISPLAY_DOLLAR_MATH_RE, true, counter, false);
            result = replaceMath(result, INLINE_PAREN_MATH_RE, false, counter, false);
            result = replaceMath(result, INLINE_DOLLAR_MATH_RE, false, counter, true);
            return result;
        })
        .join('');
}

export function prepareMarkdownForRender(markdown: string): string {
    return renderMathInMarkdown(markdown);
}
